import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { Typography } from "antd";
import { TypeWellInputTableMode } from "constants/forecasts.constants";
import { setTypeWellSettings } from "store/features/userSettings/userSettingsSlice";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import { isRatio, productStringToProductTypeEnum } from "utils/arps";

import useBetaFeatures from "hooks/useBetaFeatures";

import { getFilteredDefaultTypeWellProducts } from "api/useTypeWellSettings";
import { setDefaultTypeWell } from "api/userDefaults";

import { SegmentTemplate } from "components/arps/models/SegmentTemplate";
import {
  convertDeclineToNominal,
  convertNominalToDeclineType
} from "components/arps/utils/arpsUtils";
import { getDeclineTitleWithType } from "components/arps/utils/declineHelpers";
import { BaseButton, Tooltip } from "components/base";
import { ArpInput } from "components/shared";
import { useUserSettings } from "components/user/hooks";

import { EPSILON, isApproximatelyLessThan } from "../../../../utils/numbers";

const { Title } = Typography;

export default function TypeWellSettings() {
  const dispatch = useDispatch();
  const { hasFeature } = useBetaFeatures();
  const typeWellSettings = useSelector(
    (state: RootState) => state.userSetting.typeWellSettings
  );
  const { declineType } = useUserSettings();

  const filteredDefaults = getFilteredDefaultTypeWellProducts(hasFeature);

  const [segmentTemplate, setSegmentTemplate] = useState<SegmentTemplate>();
  const segmentRef = useRef(segmentTemplate);

  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (!typeWellSettings || !declineType) {
      return;
    }

    handleReload();
  }, [typeWellSettings, declineType]);

  const handleReload = () => {
    // Order the display by the canonical filteredDefaults order, reconciling stored
    // values by product name. The stored/server order appends missing products (see
    // useTypeWellSettings), so relying on it made columns swap between reload and reset.
    // Falling back to defaults when a stored entry is missing or its `values` object is
    // wholesale undefined also self-heals the exact corruption the old findIndex === -1
    // bug produced (it wrote `values` as `undefined`). Partial per-field corruption is
    // out of scope — the buggy path never produced it.
    const orderedSettings = filteredDefaults.map((d) => {
      const stored = typeWellSettings.settings.find((s) => s.product === d.product);
      return stored?.values ? stored : { product: d.product, values: d.values };
    });
    const segments = {
      productCols: orderedSettings.map((s) => s.product),
      name: "Name",
      productDisplayData: orderedSettings.map((s) => s.values),
      rowHeaders: filteredDefaults[0].headers,
      displayHeaders: filteredDefaults[0].displayHeaders
    } as SegmentTemplate;
    segmentRef.current = segments;
    setSegmentTemplate(segments);

    const errors = validateSegments(segments);
    setErrors(errors);
  };

  const handleArpsValueChanged = (product, header, val) => {
    const segmentIndex = segmentTemplate.productCols.findIndex(
      (name) => name === product
    );
    const segments = {
      ...segmentTemplate,
      productDisplayData: segmentTemplate.productDisplayData.map(
        (data: { [field: string]: number }, idx) => {
          if (idx === segmentIndex) {
            const copiedData = { ...data };
            if (header === "Di" || header === "Df") {
              copiedData[header] = convertDeclineToNominal({
                declineValuePercent: val,
                // Di/Df pair with Bhyp — that's the b the forecast segment carries
                // (InitialSegment sets b: Bhyp) and the b the type-well editor reads
                // back with. Must match the read side below + the editor, else the
                // secant value won't round-trip (was reading copiedData.b, undefined).
                bValue: Number(copiedData.Bhyp),
                type: declineType?.decline
              });
            } else {
              copiedData[header] = val;
            }

            return copiedData;
          }

          return data;
        }
      )
    } as SegmentTemplate;
    if (JSON.stringify(segments) !== JSON.stringify(segmentRef.current)) {
      setSegmentTemplate(segments);
      saveSettings(segments);
      segmentRef.current = segments;
    }
  };

  const validateSegments = (arpsSegment) => {
    const errors = [];
    arpsSegment.productCols.forEach((product, index) => {
      const segment = arpsSegment.productDisplayData[index];
      const lowercaseProduct = product.toLowerCase();
      if (
        segment.Qf &&
        isApproximatelyLessThan(segment.Qi, segment.Qf, EPSILON) &&
        segment.Di > 0
      ) {
        errors.push(`Invalid ${lowercaseProduct} segment: Qi cannot be less than Qf.`);
      }
      if (0 === segment.Q0 && 0 === segment.Qi && 0 === segment.Qf) {
        errors.push(`Invalid ${lowercaseProduct} segment: All flow rates are zero.`);
      }
      if (
        segment.Qf &&
        isApproximatelyLessThan(segment.Qf, segment.Qi, EPSILON) &&
        segment.Di < 0
      ) {
        errors.push(
          `Invalid ${lowercaseProduct} segment: negative decline requires Qf to be greater than Qi.`
        );
      }
    });
    return errors;
  };

  const saveSettings = async (segments) => {
    /* Validate the segments, and let the user know if there are any errors.
       Note: If there are errors, the user default settings will still be saved.
             During the creation of a type well, the errors will appear again,
             and the user cannot save the type well until the errors are resolved.
     */
    const validationErrors = validateSegments(segments);
    if (validationErrors.length === 0) {
      setErrors([]);
    } else {
      setErrors(validationErrors);
    }

    // Persist exactly what is displayed (in canonical order), then re-append any stored
    // products that aren't currently displayed (e.g. feature-gated) so their values are
    // preserved rather than dropped or written as `undefined` (the old code indexed with
    // findIndex, which returned -1 for products missing from `segments`).
    const displayed = segments.productCols.map((product, i) => ({
      product,
      headers: segments.rowHeaders,
      values: segments.productDisplayData[i]
    }));
    const preserved = typeWellSettings.settings.filter(
      (s) => !segments.productCols.includes(s.product)
    );
    const settings = {
      ...typeWellSettings,
      settings: [...displayed, ...preserved]
    };

    if (JSON.stringify(settings) === JSON.stringify(typeWellSettings)) {
      return;
    }

    const result = await setDefaultTypeWell(settings);
    if (result) {
      dispatch(setTypeWellSettings(settings));
      return;
    }
    toast.error("Error saving Type Well Settings.");
  };

  const resetToDefault = () => {
    const segments = {
      productCols: filteredDefaults.map((s) => s.product),
      name: "Name",
      productDisplayData: filteredDefaults.map((s) => s.values),
      rowHeaders: filteredDefaults[0].headers,
      displayHeaders: filteredDefaults[0].displayHeaders
    } as SegmentTemplate;

    setSegmentTemplate(segments);
    saveSettings(segments);
    segmentRef.current = segments;
  };

  function getProductInputs(product: string, i: number, forecastConstant: boolean) {
    const segIndex = segmentTemplate.productCols.findIndex((c) => c === product);
    const productData = segmentTemplate.productDisplayData[segIndex] as {
      [key: string]: string;
    };
    const convertedData = {
      ...productData,
      // Di/Df convert with Bhyp — the b the forecast segment carries (InitialSegment)
      // and the b the type-well editor reads back with. The default templates are
      // 2-segment, where the hyperbolic segment's Bhyp governs both Di and Df. If a
      // default ever becomes 3-segment (Df on a different-b segment), revisit this.
      Di: convertNominalToDeclineType({
        nominalDeclineValueDecimal: Number(productData.Di),
        bValue: Number(productData.Bhyp),
        type: declineType?.decline
      }),
      Df: convertNominalToDeclineType({
        nominalDeclineValueDecimal: Number(productData.Df),
        bValue: Number(productData.Bhyp),
        type: declineType?.decline
      })
    };

    return (
      <ProductColumnWrapper key={product + "" + i} width={120}>
        <ProductHeaderContainer>
          <SegmentHeader data-testid="typewell-product-header" data-product={product}>
            {product}
          </SegmentHeader>
        </ProductHeaderContainer>
        <ColumnContainer>
          {(segmentTemplate?.rowHeaders ?? []).map((header) => (
            <RowWrapper key={header + i}>
              <ArpInput
                header={header}
                product={productStringToProductTypeEnum(product)}
                mode={TypeWellInputTableMode.Product}
                segmentData={{
                  ...convertedData,
                  [header]: Math.round(convertedData[header] * 1000000) / 1000000 // Rounding to six decimal places to ensure values like .999999 are rounded up.
                }}
                isCalculated={false}
                isForecastConstant={forecastConstant}
                isRatio={false}
                isSync={false}
                handleReload={null}
                handleReset={null}
                handleSave={null}
                onChange={(val) => {
                  handleArpsValueChanged(product, header, val);
                }}
                typeWellTemplates={null}
                productSegmentTemplateName={null}
                fieldExists={true}
                inputRef={null}
                onBlur={null}
                isDisabled={false}
                kind={null}
              />
            </RowWrapper>
          ))}
        </ColumnContainer>
      </ProductColumnWrapper>
    );
  }

  return (
    <RootContainer>
      <HeaderContainer>
        <Title level={5}>Arps Type Well Parameters</Title>
        <HeaderActionContainer>
          <Tooltip title={"Reset to default values"} placement="top">
            <BaseButton
              className="base-button"
              appearance="subtle"
              onClick={resetToDefault}>
              Reset
            </BaseButton>
          </Tooltip>
        </HeaderActionContainer>
      </HeaderContainer>
      {errors.length > 0 && (
        <div>
          {errors.map((error, index) => (
            <div key={index} style={{ color: "red" }}>
              {error}
            </div>
          ))}
        </div>
      )}
      {segmentTemplate && (
        <ContentContainer>
          <SectionBody>
            <Wrapper>
              <TableContainer>
                <ProductColumnContainer>
                  <ProductColumnWrapper>
                    <ColumnContainer>
                      {/*Header Column*/}
                      <RowHeading />
                      {(segmentTemplate?.rowHeaders ?? []).map((header, idx) => {
                        // Normal text will be before the dot
                        // Subscript text will be after the dot
                        const displayHeader =
                          segmentTemplate.displayHeaders[idx].split(".");

                        const head =
                          header === "Df" || header === "Di"
                            ? getDeclineTitleWithType(header, declineType?.decline)
                            : header;
                        const headerUnit = head.split(" ");
                        return (
                          <RowWrapper key={header}>
                            <RowHeading className="header" width={80}>
                              {displayHeader[0]}{" "}
                              {displayHeader.length > 1 ? (
                                <sub>
                                  {displayHeader[1]}{" "}
                                  {headerUnit.length > 1 ? headerUnit[1] : null}
                                </sub>
                              ) : null}
                            </RowHeading>
                          </RowWrapper>
                        );
                      })}
                    </ColumnContainer>
                  </ProductColumnWrapper>

                  {/*Product Columns*/}
                  {(segmentTemplate?.productCols ?? [])
                    .filter((p) => !isRatio(p))
                    .map((product, i) => getProductInputs(product, i, false))}
                </ProductColumnContainer>
              </TableContainer>
            </Wrapper>
          </SectionBody>
        </ContentContainer>
      )}
    </RootContainer>
  );
}

//Styled Components
const RootContainer = styled.div`
  height: 100%;
`;

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
  height: 100%;
  white-space: nowrap;

  .overall-template-select {
    min-width: 250px;
  }

  .product-select {
    .ant-select {
      min-width: 180px;
    }
  }
`;

const HeaderActionContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  border: 0;
  justify-content: flex-end;

  cursor: pointer;
  background: transparent;
  color: var(--color-primary);
  font-weight: var(--fontWeightMedium);

  &:hover {
    color: var(--color-text);
  }

  .ant-btn {
    min-width: 100px;
    justify-content: center;
    min-height: 40px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  min-width: 750px;
  max-width: 900px;
  overflow: auto;
`;

const ContentContainer = styled.div`
  min-width: 750px;
  max-width: 900px;
  overflow: auto;
`;

const SectionBody = styled.div`
  padding: 33px 0;
`;

const TableContainer = styled.div`
  font-size: 1.3rem;

  .ant-btn {
    max-height: 26px;
  }

  .ant-select {
    height: 26px;
    font-size: 1.3rem;

    .ant-select-selection-search {
      display: flex;
    }

    .ant-select-selection-item {
      display: flex;
      align-items: center;
    }

    .ant-select-selector {
      height: 26px;
    }
  }

  .ant-input {
    padding: 0 3px;
    font-size: 1.3rem;
    height: 27px;
  }

  .ant-picker {
    padding: 0 5px;
  }

  .ant-picker-input > input {
    font-size: 1.3rem;
    height: 25px;
  }

  .sticky-col {
    position: sticky;
  }
`;

const ProductColumnContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
`;

const ProductColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 110px;
  max-width: 110px;
  min-width: 110px;
  justify-content: flex-start;

  &:first-child {
    min-width: 80px;
    max-width: 80px;
    position: sticky;
    left: 0;
    z-index: 10;
    height: 28px;
    width: 100%;
    display: inline-block;
  }
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const RowHeading = styled.div`
  min-width: 80px;
  width: 80px;
  max-width: 80px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-height: 27px;
  padding-right: 5px;

  &:last-of-type {
    min-height: 26px;
  }
`;

const RowWrapper = styled.div`
  padding-top: 7px;
`;

const ProductHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  .ant-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 26px !important;
    width: 26px;
    height: 26px;

    &.ant-btn-circle {
      border-radius: 50%;
    }

    &:hover {
      background-color: var(--color-primary);
      color: var(--color-accent);
    }
  }

  .ant-select {
    width: 100px;
  }

  gap: 2px;
`;

const SegmentHeader = styled.div`
  width: 100%;
  text-align: left;
  font-weight: bold;
`;
